import React from 'react';
import styled from 'styled-components';
import { white, slate3, slate2pt5, slate1 } from '@engage/colors';
import { spaces } from '@engage/styles';
import { label14, label12 } from '@engage/typography/font-styles';
import { useTiering, TieringCopy } from '@members/copy';
import { Tiering } from '@members/copy/models.generated/Tiering';
import { useRemainingClassesQuota } from '../hooks/useRemainingClassesQuota';
import InformationIcon from '../icons/InformationIcon';
import { useTierContext } from '../TierProvider';

type QuotaInformationProps = { margin: string; border: string; source: string };

export const QuotaInformationSection: React.FC<
  React.PropsWithChildren<QuotaInformationProps>
> = ({ margin, border, source }) => {
  const { modal, setModal, refetch } = useTierContext();

  React.useEffect(() => {
    refetch();
  }, []);
  const quotaCount = useRemainingClassesQuota();

  const cardioEquipmentClassCopy = useTiering(Tiering.CardioEquipmentCount, {
    numRemainingClasses: quotaCount,
  });

  const youHaveSingularPluralCopy = useTiering(Tiering.YouHaveSingularPlural, {
    classes: quotaCount,
  });

  const classesAvailableCopy = useTiering(Tiering.CardioEquipmentClassesAvailable, {
    youHave: youHaveSingularPluralCopy,
    classes: cardioEquipmentClassCopy,
  });

  const handleOnClick = () => {
    if (quotaCount === 0) {
      setModal({
        ...modal,
        modalType: 'no_quota_available_modal',
        isOpen: true,
        source: source,
      });
    } else {
      setModal({
        ...modal,
        modalType: 'quota_available_modal',
        isOpen: true,
        source: source,
      });
    }
  };

  return (
    <Content
      data-test-id="quotaBanner"
      onClick={handleOnClick}
      role="button"
      tabIndex={0}
      onKeyDown={() => {}}
      margin={margin}
      border={border}
    >
      <ContentCopy data-test-id="classesAvailableText">
        <ClassesRemainingCopy>{classesAvailableCopy}</ClassesRemainingCopy>
        <TieringCopy id={Tiering.TakeClassesWithAnyEquipment} />
      </ContentCopy>
      <StyledInfoIcon />
    </Content>
  );
};

const Content = styled.div<{ margin?: string; border?: string }>`
  display: flex;
  aligns-items: center;
  justify-content: space-between;
  padding: ${spaces.small}px ${spaces.medium}px;
  background-color: ${white};
  border-radius: ${spaces.xxSmall}px;
  cursor: pointer;
  margin: ${props => (props.margin ? props.margin : '')};
  border: ${props => (props.border ? props.border : '')};
`;

const ContentCopy = styled.div`
  display: flex;
  flex-direction: column;
  *:: {
    text-transform: lowercase;
    &::first-letter {
      text-transform: uppercase;
    }
  }
  ${label12}
  color: ${slate1};
`;

const ClassesRemainingCopy = styled.span`
  ${label14}
  color: ${slate2pt5};
`;

const StyledInfoIcon = styled(InformationIcon)`
  color: ${slate3};
  width: 20px;
`;
